import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import { THEME_STORAGE_KEY, DARK_MODE_ENABLED, resolveTheme, isExplicitTheme } from '../lib/theme/resolveTheme';
import { mediaQueryMatches, safeMatchMedia, subscribeMediaQuery } from '../lib/safeMatchMedia';

const ThemeContext = createContext();

function readStoredTheme() {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null; // private browsing / storage disabled — fall back to system
  }
}

function systemPrefersDark() {
  return mediaQueryMatches('(prefers-color-scheme: dark)', false);
}

// Exposed as `applyTransientTheme`: sets data-theme + React state WITHOUT
// persisting to localStorage. Used only by the guest discovery pages (#3756)
// to match the marketing site's prefers-color-scheme behaviour.
// Non-throwing variant: null outside a ThemeProvider (isolated page tests).
export function useThemeOptional() {
  return useContext(ThemeContext) || null;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

/**
 * Issue #1630 — theme activation + persisted manual override.
 *
 * The blocking inline script in index.html already resolved and set
 * data-theme on <html> before React mounted (no Flash-of-Light-Theme —
 * Gemini P1). This provider:
 *  - picks up that same resolved value for React state (so components can
 *    read `theme` without a DOM read),
 *  - keeps `data-theme` in sync live with the OS preference while the
 *    learner hasn't set an explicit override (decision log item 3:
 *    "follow the device... with a manual override"),
 *  - exposes setTheme()/toggleTheme() for the settings-page control, which
 *    persists the choice to localStorage so it survives reloads.
 *
 * BrandingContext reads `theme` from here (not from the DOM) so its
 * effect re-runs — and re-applies or clears its brand-token overrides —
 * whenever the theme changes at runtime, not just on mount.
 */
export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    if (typeof document !== 'undefined') {
      const attr = document.documentElement.getAttribute('data-theme');
      // #4194: a stale data-theme="dark" is not adopted while dark mode is off.
      if (attr === 'light' || (attr === 'dark' && DARK_MODE_ENABLED)) return attr;
    }
    return resolveTheme(readStoredTheme(), systemPrefersDark());
  });
  const [hasExplicitPreference, setHasExplicitPreference] = useState(() =>
    isExplicitTheme(readStoredTheme())
  );

  // The single React-side writer of data-theme. #4194: while dark mode is off
  // (DARK_MODE_ENABLED in resolveTheme.js) every caller — setTheme,
  // clearOverride, toggleTheme, applyTransientTheme and the OS listener — is
  // coerced to 'light' here, so no per-caller check is needed.
  const applyTheme = useCallback((requested) => {
    const next = DARK_MODE_ENABLED ? requested : 'light';
    setThemeState(next);
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', next);
    }
  }, []);

  // #4194: on mount, overwrite any stale data-theme="dark" left on <html>
  // (e.g. set by an older cached pre-mount script). Stored 'tacit-theme' is
  // deliberately left alone so the overhaul can restore the learner's choice.
  useEffect(() => {
    if (!DARK_MODE_ENABLED) applyTheme('light');
  }, [applyTheme]);

  // Follow the OS preference live, but only for an explicit Match-device
  // choice (stored 'system'). INTERIM: never-touched users are pinned
  // light (see resolveTheme.js ship-gate note), so they must not be
  // live-flipped by OS changes either.
  useEffect(() => {
    if (!DARK_MODE_ENABLED || hasExplicitPreference) {
      return undefined;
    }
    if (readStoredTheme() !== 'system') {
      return undefined;
    }
    // #4215: null (absent/throwing matchMedia) -> no listener, no throw.
    const mql = safeMatchMedia('(prefers-color-scheme: dark)');
    if (!mql) return undefined;
    const handleChange = (e) => applyTheme(e.matches ? 'dark' : 'light');
    return subscribeMediaQuery(mql, handleChange);
  }, [hasExplicitPreference, applyTheme]);

  const setTheme = useCallback((next) => {
    if (next !== 'light' && next !== 'dark') return;
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Storage unavailable — theme still applies for this session.
    }
    setHasExplicitPreference(true);
    applyTheme(next);
  }, [applyTheme]);

  const clearOverride = useCallback(() => {
    // Match-device is stored EXPLICITLY as 'system' (not key-removal):
    // under the interim light-default, unset means light — only a stored
    // 'system' opts into following the OS.
    try {
      localStorage.setItem(THEME_STORAGE_KEY, 'system');
    } catch {
      // ignore
    }
    setHasExplicitPreference(false);
    applyTheme(resolveTheme('system', systemPrefersDark()));
  }, [applyTheme]);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  const value = useMemo(() => ({
    theme,
    isDark: theme === 'dark',
    hasExplicitPreference,
    setTheme,
    toggleTheme,
    clearOverride,
    applyTransientTheme: applyTheme,
  }), [theme, hasExplicitPreference, setTheme, toggleTheme, clearOverride, applyTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}
